'use client';

import { useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { readLoyaltyOrderSnapshot } from '@instamenu/context-checkout';
import { LoyaltyCard3D } from './loyalty-card-3d';
import { card3dSlideIn } from './loyalty-card-3d.css';
import { useLoyaltyInit } from './use-loyalty-init';
import { makePath } from '../utils';

type DisplaySnapshot = {
  optimisticBalance: number;
  optimisticProgress: number;
  optimisticRewards: number;
  earnedFromOrder: number;
  redeemedThisOrder: number;
  isItemBased: boolean;
  earningThreshold: number;
  programDisplayName: string | null;
};

export const CompleteLoyalty = observer(() => {
  const { checkout, loyalty, user } = useLoyaltyInit();
  const router = useRouter();
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });

  // Lock in the displayed numbers the first render where loyalty data
  // is ready, and reuse them for the lifetime of the component. Two
  // sources, in priority order:
  //
  //   1. The pre-checkout snapshot saved at payment success
  //      (`Order.onPaymentSucceeded` → `LoyaltyStore.snapshotForOrder`).
  //      Stored in sessionStorage keyed by order id, so it survives
  //      in-tab refreshes of the order-complete screen. This is the
  //      "warm flow" — the user just paid; the snapshot is the
  //      pre-payment baseline plus the cart's earn/redeem deltas
  //      computed against it. Reading the live `loyalty.*` here would
  //      double-count once the server's post-payment status has
  //      propagated back.
  //
  //   2. Raw server values from `loyalty.*` (no optimistic delta).
  //      This is the "cold load" path — direct link, different tab,
  //      payment recovery — where there's no in-session baseline to
  //      reason from, so we trust the server. The "+N earned this
  //      order" celebration copy is suppressed because we can't
  //      derive an accurate delta without a baseline.
  const snapshotRef = useRef<DisplaySnapshot | null>(null);
  const ready = !!(loyalty?.isActive && loyalty.status && !loyalty.justEnrolled);
  if (ready && snapshotRef.current === null && loyalty) {
    const orderId = checkout?.order.id;
    const saved = orderId ? readLoyaltyOrderSnapshot(orderId) : null;
    if (saved) {
      snapshotRef.current = saved;
    } else {
      // Cold-load fallback. Display whatever the server currently
      // reports as authoritative; suppress the per-order delta copy.
      snapshotRef.current = {
        optimisticBalance: loyalty.pointsBalance,
        optimisticProgress: loyalty.progress,
        optimisticRewards: loyalty.rewardsAvailable,
        earnedFromOrder: 0,
        redeemedThisOrder: 0,
        isItemBased: loyalty.isItemBased,
        earningThreshold: loyalty.earningThreshold,
        programDisplayName: loyalty.program?.displayName ?? null,
      };
    }
  }

  if (!user || !ready || !snapshotRef.current) return null;

  const snap = snapshotRef.current;

  const handleClick = () => {
    if (snap.optimisticRewards > 0 && checkout?.order.restaurant.id) {
      router.push(
        makePath({
          restaurantId: checkout.order.restaurant.id,
          tableCode: checkout.order.table || undefined,
          menuId: checkout.menuId,
        }),
      );
    }
  };

  const action = isDesktop ? 'Click' : 'Tap';
  const earnedSuffix =
    snap.earnedFromOrder > 0
      ? snap.isItemBased
        ? `\n${snap.optimisticProgress}/${snap.earningThreshold} toward your next free item`
        : `\n${snap.optimisticBalance} pts toward your next free item`
      : '';
  const rewardLabel =
    snap.optimisticRewards > 0
      ? snap.isItemBased
        ? `Free item ready! ${action} to redeem${earnedSuffix}`
        : `Redeem your points! ${action} to pick a free item${earnedSuffix}`
      : snap.earnedFromOrder > 0
        ? snap.isItemBased
          ? `+${snap.earnedFromOrder} ${snap.earnedFromOrder !== 1 ? 'punches' : 'punch'} this order`
          : `+${snap.earnedFromOrder} pts this order`
        : undefined;

  return (
    <div className={card3dSlideIn}>
      <LoyaltyCard3D
        programName={snap.programDisplayName ?? 'Rewards'}
        rewardsAvailable={snap.optimisticRewards}
        pointsBalance={snap.optimisticBalance}
        accentColor={checkout?.order.restaurant.restaurantGroup?.color ?? undefined}
        isItemBased={snap.isItemBased}
        earningThreshold={snap.earningThreshold}
        progress={snap.optimisticProgress}
        onClick={snap.optimisticRewards > 0 ? handleClick : undefined}
        rewardLabel={rewardLabel}
      />
    </div>
  );
});
